import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faFilePen,
  faRadar,
  faWandMagicSparkles,
} from '@fortawesome/pro-light-svg-icons';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import {
  UseOfForceScreeningFindingModel,
  UseOfForceScreeningModel,
} from '../../../../api-service/clients';
import { CardComponent } from '../../../../components/card-component';
import { ColorPalette } from '../../../../components/config';
import { formatDate, formatDateTime } from '../../../../utilities';
import {
  interventionTypeShortLabels,
  interventionTypesWithAuthority,
  interventionTypesWithoutAuthority,
} from '../use-of-force/use-of-force-labels';
import { useAiServiceProviderDemoAccess } from './ai-demo-access';
import { AiFeatureInfo } from './ai-feature-info';

const client = moduleClientFactory.createCitizenServiceProviderAiDemoClient();
const aiAccent = '#4527a0';

interface UseOfForceRadarCardProps {
  citizenId: string;
  serviceProviderUnitId: string;
  canRegister: boolean;
  onRegister: (interventionStartedAt: Date | string) => void;
  onOpenIncident: (incidentId: string) => void;
}

export function UseOfForceRadarCard(props: Readonly<UseOfForceRadarCardProps>) {
  const hasAccess = useAiServiceProviderDemoAccess();
  if (!hasAccess) {
    return null;
  }

  return <UseOfForceRadar {...props} />;
}

function UseOfForceRadar({
  citizenId,
  serviceProviderUnitId,
  canRegister,
  onRegister,
  onOpenIncident,
}: Readonly<UseOfForceRadarCardProps>) {
  const [screening, setScreening] = useState<UseOfForceScreeningModel>();
  const [isScreening, setIsScreening] = useState(false);
  const [dismissedNoteIds, setDismissedNoteIds] = useState<string[]>([]);
  const [showRegistered, setShowRegistered] = useState(false);

  async function screen() {
    setIsScreening(true);
    try {
      const response = await client.screenCitizenDiaryForUseOfForce({
        citizenId,
        serviceProviderUnitId,
      });
      setScreening(response.screening);
      setDismissedNoteIds([]);
      setShowRegistered(false);
    } finally {
      setIsScreening(false);
    }
  }

  const unregistered =
    screening?.findings.filter(
      finding =>
        !finding.matchingIncidentId &&
        !dismissedNoteIds.includes(finding.diaryNoteId)
    ) ?? [];
  const registered =
    screening?.findings.filter(finding => finding.matchingIncidentId) ?? [];

  return (
    <Box sx={{ mb: 2 }}>
      <CardComponent
        title="Magtanvendelses-radar"
        contentBackgroundColor={ColorPalette.WHITE}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            color: aiAccent,
          }}
        >
          <FontAwesomeIcon icon={faRadar} />
          <Chip
            size="small"
            icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            label="AI-forslag"
            sx={{
              backgroundColor: '#ede7f6',
              color: aiAccent,
              '& .MuiChip-icon': { color: aiAccent },
            }}
          />
          <AiFeatureInfo feature="useOfForceRadar" />
        </Box>
        {!screening && !isScreening && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              Gennemgå enhedens dagbogsnotater fra de seneste 30 dage og find
              episoder, der kan være en magtanvendelse uden et Skema 1.
            </Typography>
            <Button
              variant="contained"
              onClick={screen}
              startIcon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            >
              Gennemgå dagbogen
            </Button>
          </Box>
        )}

        {isScreening && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Læser dagbogsnotaterne …</Typography>
          </Box>
        )}

        {screening && !isScreening && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Gennemgik {screening.notesScreened} dagbogsnotater siden{' '}
              {formatDate(screening.periodStart)}.{' '}
              <strong>
                {unregistered.length === 0
                  ? 'Ingen mulige magtanvendelser uden skema.'
                  : `${unregistered.length} ${
                      unregistered.length === 1
                        ? 'mulig magtanvendelse'
                        : 'mulige magtanvendelser'
                    } uden Skema 1.`}
              </strong>
            </Typography>

            {unregistered.map(finding => (
              <FindingRow
                key={finding.diaryNoteId}
                finding={finding}
                actions={
                  <>
                    <Button
                      size="small"
                      onClick={() =>
                        setDismissedNoteIds(current => [
                          ...current,
                          finding.diaryNoteId,
                        ])
                      }
                    >
                      Ikke en magtanvendelse
                    </Button>
                    {canRegister && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faFilePen} />}
                        onClick={() => onRegister(finding.occurredAt)}
                      >
                        Opret Skema 1
                      </Button>
                    )}
                  </>
                }
              />
            ))}

            {registered.length > 0 && (
              <>
                <Button
                  size="small"
                  onClick={() => setShowRegistered(current => !current)}
                  startIcon={
                    <FontAwesomeIcon
                      icon={showRegistered ? faChevronUp : faChevronDown}
                    />
                  }
                  sx={{ mb: 1 }}
                >
                  {registered.length} med Skema 1
                </Button>
                <Collapse in={showRegistered} unmountOnExit>
                  {registered.map(finding => (
                    <FindingRow
                      key={finding.diaryNoteId}
                      finding={finding}
                      muted
                      actions={
                        <Button
                          size="small"
                          onClick={() =>
                            onOpenIncident(finding.matchingIncidentId!)
                          }
                        >
                          Åbn Skema 1
                        </Button>
                      }
                    />
                  ))}
                </Collapse>
              </>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                AI peger kun på notater. Lederen vurderer, om der er tale om en
                magtanvendelse.
              </Typography>
              <Button size="small" onClick={screen}>
                Gennemgå igen
              </Button>
            </Box>
          </>
        )}
      </CardComponent>
    </Box>
  );
}

function interventionTypeLabel(finding: UseOfForceScreeningFindingModel) {
  const type = finding.suggestedInterventionType;
  if (!type) {
    return undefined;
  }
  return {
    short: interventionTypeShortLabels[type],
    long:
      interventionTypesWithAuthority[type] ??
      interventionTypesWithoutAuthority[type] ??
      interventionTypeShortLabels[type],
  };
}

interface FindingRowProps {
  finding: UseOfForceScreeningFindingModel;
  actions: React.ReactNode;
  muted?: boolean;
}

function FindingRow({ finding, actions, muted }: Readonly<FindingRowProps>) {
  const typeLabel = interventionTypeLabel(finding);

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
        opacity: muted ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formatDateTime(finding.occurredAt)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[finding.title, finding.author].filter(Boolean).join(' · ')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {muted && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Skema 1 findes"
          />
        )}
        {!muted && (
          <Chip
            size="small"
            variant="outlined"
            label={finding.isHighConfidence ? 'Tydeligt indgreb' : 'Uklart'}
            sx={{
              borderColor: finding.isHighConfidence ? '#c62828' : '#b26a00',
              color: finding.isHighConfidence ? '#c62828' : '#b26a00',
            }}
          />
        )}
        {typeLabel && (
          <Tooltip title={typeLabel.long}>
            <Chip size="small" label={typeLabel.short} />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          borderLeft: `4px solid ${aiAccent}`,
          backgroundColor: '#f6f3fb',
          px: 1.5,
          py: 1,
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          »{finding.quote}«
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {finding.reason}
        </Typography>
        {actions}
      </Box>
    </Box>
  );
}
